import React from 'react';
import Clarity from '@microsoft/clarity';

import { useFirmId } from '../api/hooks/useFirmId';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { isServer } from '../utils/window';

export interface IClarityContext {
  consentIsKnown: boolean;
  clarityIsInitialised: boolean;
  giveConsent: () => void;
  declineConsent: () => void;
}

export const ClarityContext = React.createContext<IClarityContext>({
  consentIsKnown: false,
  clarityIsInitialised: false,
  giveConsent: () => {},
  declineConsent: () => {},
});

enum AnalyticsConsent {
  Granted = 'granted',
  Denied = 'denied',
}

export const CONSENT_KEY = 'analytics_consent';

interface IClarityProvider {
  clarityProjectId?: string;
  userId?: string;
}

const safeLocalStorageGet = (key: string): string | null => {
  if (isServer) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  if (isServer) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // do nothing
  }
};

const isKnownConsent = (value: string | null): value is AnalyticsConsent => {
  return value === AnalyticsConsent.Granted || value === AnalyticsConsent.Denied;
};

const applyConsentToClarity = (consent: AnalyticsConsent): void => {
  Clarity.consentV2({
    ad_Storage: AnalyticsConsent.Denied,
    analytics_Storage: consent,
  });
};

const readStoredConsent = (): AnalyticsConsent | null => {
  const localStorageConsent = safeLocalStorageGet(CONSENT_KEY);
  return isKnownConsent(localStorageConsent) ? localStorageConsent : null;
};

export const ClarityProvider: React.FC<React.PropsWithChildren<IClarityProvider>> = ({
  children,
  userId,
  clarityProjectId,
}) => {
  const firmId = useFirmId();
  const { hasFeatureFlag } = useFeatureFlag();
  const isUserAnalyticsEnabled = hasFeatureFlag('seccl-19639-user-analytics');

  const canRunClarity = Boolean(firmId && userId && isUserAnalyticsEnabled && !isServer);

  const [consentIsKnown, setConsentIsKnown] = React.useState(() => {
    return readStoredConsent() !== null;
  });

  const [clarityIsInitialised, setClarityIsInitialised] = React.useState(false);
  const initOnceRef = React.useRef(false);
  const consentAppliedOnceRef = React.useRef(false);

  React.useEffect(() => {
    if (!canRunClarity || initOnceRef.current || !clarityProjectId) return;

    initOnceRef.current = true;

    Clarity.init(clarityProjectId);
    Clarity.setTag('firm_id', firmId!);

    setClarityIsInitialised(true);

    const storedConsent = readStoredConsent();
    if (!consentAppliedOnceRef.current && storedConsent) {
      consentAppliedOnceRef.current = true;
      applyConsentToClarity(storedConsent);
      setConsentIsKnown(true);
    }
  }, [canRunClarity, firmId, userId]);

  const declineConsent = React.useCallback(() => {
    if (!canRunClarity) return;

    applyConsentToClarity(AnalyticsConsent.Denied);
    safeLocalStorageSet(CONSENT_KEY, AnalyticsConsent.Denied);

    setConsentIsKnown(true);
    consentAppliedOnceRef.current = true;
  }, [canRunClarity]);

  const giveConsent = React.useCallback(() => {
    if (!canRunClarity) return;

    applyConsentToClarity(AnalyticsConsent.Granted);
    safeLocalStorageSet(CONSENT_KEY, AnalyticsConsent.Granted);

    setConsentIsKnown(true);
    consentAppliedOnceRef.current = true;
  }, [canRunClarity]);

  if (!canRunClarity) {
    return <>{children}</>;
  }

  return (
    <ClarityContext.Provider value={{ consentIsKnown, clarityIsInitialised, declineConsent, giveConsent }}>
      {children}
    </ClarityContext.Provider>
  );
};
